import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Terminal,
  BookOpen,
  Layout,
  Copy,
  Check,
  BookMarked,
  MessageCircle,
  FileText,
  Clock,
  Star,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { courseApi } from '../../services/courseApi';
import './CourseDetail.css';

const MarkdownRenderer = ({ content }) => {
  const [copiedCode, setCopiedCode] = useState(null);

  const handleCopy = async (text, index) => {
    await navigator.clipboard.writeText(text);
    setCopiedCode(index);
    setTimeout(() => setCopiedCode(null), 2000);
  };
  const tabItems = [
    { id: 'content', icon: FileText, label: 'Course Content', gradient: 'from-blue-500 to-blue-600' },
    { id: 'quiz', icon: BookMarked, label: 'Quiz', gradient: 'from-purple-500 to-purple-600' },
    { id: 'interview', icon: MessageCircle, label: 'Interview Questions', gradient: 'from-pink-500 to-pink-600' }
  ];
  return (
    <ReactMarkdown 
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      className="markdown-content"
      components={{
        h1: ({ children }) => (
          <h1 className="text-3xl font-bold text-white mt-12 mb-6 font-display">
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-2xl font-semibold text-white mt-10 mb-4 pl-4 border-l-4 border-blue-500">
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-xl font-medium text-blue-400 mt-8 mb-3">
            {children}
          </h3>
        ),
        h4: ({ children }) => (
          <h4 className="text-lg font-medium text-gray-200 mt-6 mb-2">
            {children}
          </h4>
        ),
        p: ({ children }) => (
          <p className="text-gray-300 leading-relaxed mb-4 [&:not(:first-child)]:mt-4">
            {children}
          </p>
        ),
        strong: ({ children }) => (
          <strong className="font-semibold text-white">
            {children}
          </strong>
        ),
        ul: ({ children }) => (
          <ul className="text-gray-300 space-y-2 my-4 ml-6 list-disc marker:text-blue-400">
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="text-gray-300 space-y-2 my-4 ml-6 list-decimal marker:text-blue-400">
            {children}
          </ol>
        ),
        li: ({ children }) => (
          <li className="text-gray-300 leading-relaxed">
            {children}
          </li>
        ),
        pre: ({ children }) => (
          <pre className="relative my-6 rounded-lg overflow-hidden">
            {children}
          </pre>
        ),
        code: ({ node, inline, className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className || '');
          const language = match ? match[1] : '';
          const codeString = String(children).replace(/\n$/, '');

          if (inline) {
            return (
              <code className="px-2 py-1 bg-gray-800 rounded font-mono text-sm text-gray-200" {...props}>
                {children}
              </code>
            );
          }

          return (
            <div className="relative group">
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-all duration-300" />
              <div className="relative">
                <div className="flex items-center justify-between bg-gray-800/90 rounded-t-lg px-4 py-2 border-b border-gray-700/50">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                    </div>
                    {language && (
                      <span className="text-sm text-gray-400 font-medium">{language}</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleCopy(codeString, node.position?.start.line)}
                    className="p-1.5 hover:bg-gray-700/50 rounded-md transition-colors"
                    aria-label={copiedCode === node.position?.start.line ? "Copied!" : "Copy code"}
                  >
                    {copiedCode === node.position?.start.line ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400 hover:text-gray-300" />
                    )}
                  </button>
                </div>
                <div className="overflow-x-auto bg-gray-800/50 rounded-b-lg">
                  <pre className="p-4 text-gray-300 text-sm font-mono">
                    {children}
                  </pre>
                </div>
              </div>
            </div>
          );
        },
        a: ({ children, href }) => (
          <a 
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-0.5"
          >
            {children}
            <ExternalLink className="w-4 h-4" />
          </a>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-4 bg-blue-500/5 rounded-r">
            <div className="text-gray-300 italic">
              {children}
            </div>
          </blockquote>
        ),
        table: ({ children }) => (
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700 border border-gray-700 rounded-lg">
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-gray-800">
            {children}
          </thead>
        ),
        th: ({ children }) => (
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 border-t border-gray-700">
            {children}
          </td>
        ),
        hr: () => (
          <hr className="my-8 border-gray-700" />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

const CourseDetail = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [content, setContent] = useState('');
  const [activeTab, setActiveTab] = useState('content');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCourse = async () => {
      try {
        setLoading(true);
        const response = await courseApi.getCourseDetails(courseId);
        if (response.success) {
          setCourse(response.data);
        } else {
          throw new Error(response.error || 'Failed to load course');
        }

        const contentResponse = await courseApi.getCourseContent(courseId);
        if (contentResponse.success) {
          setContent(contentResponse.data.content);
        }
      } catch (error) {
        console.error('Error loading course:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-3xl animate-pulse" />
          <div className="relative space-y-4 text-center">
            <div className="w-16 h-16 border-4 border-blue-500 loading-spinner rounded-full mx-auto"/>
            <p className="text-gray-400 animate-pulse font-medium">Loading course content...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4 max-w-md mx-auto px-4"
        >
          <div className="glass-card p-4">
            <p className="text-red-400 text-lg font-medium mb-2">{error}</p>
            <p className="text-gray-400">Unable to load the course content</p>
          </div>
          <Link 
            to="/learning" 
            className="inline-flex items-center gap-2 px-6 py-3 glass-container text-blue-400 hover:bg-blue-500/20 transition-all-200"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Courses
          </Link>
        </motion.div>
      </div>
    );
  }

  const tabItems = [
    { id: 'content', icon: FileText, label: 'Course Content', gradient: 'from-blue-500 to-blue-600' },
    { id: 'quiz', icon: BookMarked, label: 'Quiz', gradient: 'from-purple-500 to-purple-600' },
    { id: 'interview', icon: MessageCircle, label: 'Interview Questions', gradient: 'from-pink-500 to-pink-600' }
  ];


  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
      {/* Background patterns */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-pattern-gradient-right" />
        <div className="absolute inset-0 bg-pattern-gradient-left" />
        <div className="absolute h-full w-full bg-pattern-dots" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Navigation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Link 
            to="/learning"
            className="inline-flex items-center gap-2 px-4 py-2 glass-container hover:bg-blue-500/20 text-blue-400 group transition-all-200"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Courses</span>
          </Link>
        </motion.div>

        {/* Course Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-8"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl blur-xl" />
            <div className="relative glass-container p-8">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Course Icon */}
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 gradient-blue-purple rounded-2xl p-0.5">
                    <div className="w-full h-full bg-gray-900 rounded-[15px] flex items-center justify-center">
                      <Terminal className="w-10 h-10 text-blue-400" />
                    </div>
                  </div>
                </div>

                {/* Course Info */}
                <div className="flex-grow">
                  <h1 className="text-4xl font-bold text-white mb-4 font-display">
                    {course?.title}
                  </h1>
                  <p className="text-xl text-gray-300 mb-6">
                    {course?.description}
                  </p>

                  {/* Course Stats */}
                  <div className="flex flex-wrap gap-4">
                    <div className="inline-flex items-center gap-2 px-4 py-2 glass-container bg-blue-500/10">
                      <Layout className="w-5 h-5 text-blue-400" />
                      <span className="font-medium text-gray-300">
                        {course?.difficulty}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-2 px-4 py-2 glass-container bg-purple-500/10">
                      <Clock className="w-5 h-5 text-purple-400" />
                      <span className="font-medium text-gray-300">
                        {course?.duration || '2 hours'}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-2 px-4 py-2 glass-container bg-pink-500/10">
                      <Star className="w-5 h-5 text-pink-400" />
                      <span className="font-medium text-gray-300">
                        {course?.rating || '4.8'} / 5.0
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Course Content */}
        <div className="mt-8 glass-container overflow-hidden">
         {/* Tabs */}
  <div className="border-b border-gray-700/50 bg-gray-800/30">
    <nav className="flex gap-2 p-2">
      {tabItems.map(tab => (
        course?.[`has${tab.id.charAt(0).toUpperCase()}${tab.id.slice(1)}`] !== false && (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              relative group flex-1 px-6 py-3 flex items-center justify-center gap-2 
              rounded-xl font-medium transition-all duration-300
              ${activeTab === tab.id 
                ? 'text-white shadow-lg transform -translate-y-0.5' 
                : 'text-gray-400 hover:text-gray-200 hover:-translate-y-0.5'
              }
            `}
          >
            {/* Background gradient */}
            <div className={`
              absolute inset-0 rounded-xl transition-opacity duration-300
              bg-gradient-to-r ${tab.gradient}
              ${activeTab === tab.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-10'}
            `} />
            
            {/* Glow effect */}
            <div className={`
              absolute inset-0 rounded-xl transition-opacity duration-300
              bg-gradient-to-r ${tab.gradient} blur-xl
              ${activeTab === tab.id ? 'opacity-40' : 'opacity-0 group-hover:opacity-20'}
            `} />

            {/* Content */}
            <div className="relative flex items-center gap-2">
              <tab.icon className={`
                w-4 h-4 transition-transform duration-300
                ${activeTab === tab.id ? 'scale-110' : 'group-hover:scale-110'}
              `} />
              <span className="whitespace-nowrap">{tab.label}</span>
            </div>

            {/* Active indicator dot */}
            {activeTab === tab.id && (
              <motion.div
                layoutId="activeTabDot"
                className="absolute -bottom-1 left-1/2 w-1 h-1 rounded-full bg-white"
                initial={false}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
          </button>
        )
      ))}
    </nav>
  </div>
          {/* Tab Content */}
          <div className="p-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              key={activeTab}
              className="min-h-[300px]"
            >
              {activeTab === 'content' && (
                <div className="prose prose-invert max-w-none">
                  <MarkdownRenderer content={content} />
                </div>
              )}
              
              {activeTab === 'quiz' && (
                <div className="text-center py-12">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className="space-y-6"
                  >
                    <div className="glass-container bg-blue-500/10 p-8 max-w-2xl mx-auto">
                      <h3 className="text-2xl font-bold text-white mb-4">Ready to Test Your Knowledge?</h3>
                      <p className="text-gray-300 mb-8">
                        Take our interactive quiz to assess your understanding of the course material
                      </p>
                      <Link
                        to={`/learning/courses/${courseId}/quiz`}
                        className="inline-flex items-center gap-3 px-8 py-4 gradient-blue-purple text-white rounded-xl font-medium shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 group"
                      >
                        <BookMarked className="w-5 h-5" />
                        Start Quiz
                        <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </motion.div>
                </div>
              )}
              
              {activeTab === 'interview' && (
                <div className="text-center py-12">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className="space-y-6"
                  >
                    <div className="glass-container bg-purple-500/10 p-8 max-w-2xl mx-auto">
                      <h3 className="text-2xl font-bold text-white mb-4">Practice Interview Questions</h3>
                      <p className="text-gray-300 mb-8">
                        Prepare for technical interviews with our curated list of questions and answers
                      </p>
                      <Link
                        to={`/learning/courses/${courseId}/interview`}
                        className="inline-flex items-center gap-3 px-8 py-4 gradient-purple-pink text-white rounded-xl font-medium shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 transition-all duration-300 group"
                      >
                        <MessageCircle className="w-5 h-5" />
                        View Interview Questions
                        <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </motion.div>
                </div>
              )}
            </motion.div>
          </div>
        </div>

        {/* Prerequisites */}
        {course?.prerequisites?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8"
          >
            <div className="glass-container p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-400" />
                Prerequisites
              </h2>
              <ul className="space-y-3">
                {course.prerequisites.map((prereq, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3 text-gray-300 group"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                      <ChevronRight className="w-4 h-4 text-blue-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                    <span>{prereq}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;